import { cn } from '@myosh/odin-components';
import type { Node, NodeProps } from '@xyflow/react';
import type React from 'react';
import { useMemo } from 'react';
import { useAppPermissionsContext } from '../../../context/app-permissions.context';
import { CriticalControlEnum, EffectiveControlEnum } from '../../../helpers/node-util';
import { useAppSelector } from '../../../redux/hooks';
import { selectConsequences, selectControlNodeHasEscalationFactors, selectMoving } from '../../../redux/slices/diagram';
import BaseNode, { type BaseNodeData } from './base-node.component';
import AddEscalationFactorControl from './common/add-escalation-factor-control.component';
import HorizontalMoveControl, {
  HORIZONTAL_STEP,
  HorizontalMoveDirection,
} from './common/horizontal-move-control.component';

type MitigatingControlNodeData = BaseNodeData & {
  criticalControlType?: CriticalControlEnum;
  effectiveControlType?: EffectiveControlEnum;
};
export type MitigatingControlNodeType = Node<MitigatingControlNodeData>;
type MitigatingControlNodeProps = NodeProps<MitigatingControlNodeType>;

const defaultClassName = 'bg-mc-default-bg border-mc-default-border text-mc-default-fg';
const nonEffectiveClassName =
  'bg-mc-non_critical-non_effective-bg border-mc-non_critical-non_effective-border text-mc-non_critical-non_effective-fg';
const criticalClassName =
  'bg-mc-critical-effective-bg border-mc-critical-effective-border text-mc-critical-effective-fg';

const defaultIconClassName = 'text-mc-default-bg stroke-mc-default-fg';
const nonEffectiveIconClassName = 'text-mc-non_critical-non_effective-bg stroke-mc-non_critical-non_effective-fg';
const criticalIconClassName = 'text-mc-critical-effective-bg stroke-mc-critical-effective-fg';

const defaultGlobalIconClassName = 'text-mc-default-border';
const nonEffectiveGlobalIconClassName = 'text-mc-non_critical-non_effective-border';
const criticalGlobalIconClassName = 'text-mc-critical-effective-border';

/**
 * A component that represents a mitigating control node in a flow diagram.
 * Extends the base node component with mitigating-control-specific functionality.
 *
 * @component
 * @param {MitigatingControlNodeProps} props - The properties for the mitigating control node component
 * @returns {JSX.Element} A rendered mitigating control node component
 *
 * @example
 * ```tsx
 * <MitigatingControlNode
 *   id="mitigating control-1"
 *   data={{ label: "Mitigating Control" }}
 *   position={{ x: 100, y: 100 }}
 * />
 * ```
 */
const MitigatingControlNode = (props: MitigatingControlNodeProps): React.JSX.Element => {
  const { recordId, criticalControlType, effectiveControlType, cellIndex, rowIndex, editMode } = props.data;
  const consequences = useAppSelector(selectConsequences);
  const moving = useAppSelector(selectMoving);
  const hasEscalationFactors = useAppSelector((state) => selectControlNodeHasEscalationFactors(state, props.id));

  const {
    addNodePermission: { escalationFactors: canAddEscalationFactor },
  } = useAppPermissionsContext();

  const canMoveLeft = useMemo(() => (cellIndex !== undefined ? cellIndex >= HORIZONTAL_STEP : false), [cellIndex]);

  const canMoveRight = useMemo(
    () =>
      cellIndex !== undefined && rowIndex !== undefined && rowIndex >= 0 && rowIndex <= consequences.length - 1
        ? cellIndex < consequences[rowIndex].controls.length - HORIZONTAL_STEP
        : false,
    [cellIndex, rowIndex, consequences]
  );

  const { className, iconClassName, globalIconClassName } = useMemo(() => {
    let className = defaultClassName;
    let iconClassName = defaultIconClassName;
    let globalIconClassName = defaultGlobalIconClassName;

    if (effectiveControlType === EffectiveControlEnum.NON_EFFECTIVE) {
      className = nonEffectiveClassName;
      iconClassName = nonEffectiveIconClassName;
      globalIconClassName = nonEffectiveGlobalIconClassName;
    } else if (criticalControlType === CriticalControlEnum.CRITICAL) {
      className = criticalClassName;
      iconClassName = criticalIconClassName;
      globalIconClassName = criticalGlobalIconClassName;
    }

    className = cn(className, { 'animate-wobble': moving && (canMoveLeft || canMoveRight) });

    return { className, iconClassName, globalIconClassName };
  }, [criticalControlType, effectiveControlType, canMoveLeft, canMoveRight, moving]);

  const data = useMemo(() => {
    return {
      ...props.data,
      className,
      iconClassName,
      globalIconClassName,
      showRecordIndicators: true,
      showUnlinkActionIcon: !props.data.recordId || !hasEscalationFactors,
    };
  }, [props.data, className]);

  return (
    <div className="group flex items-center">
      <HorizontalMoveControl
        id={props.id}
        type="mitigating-control-node"
        index={cellIndex!}
        rowIndex={rowIndex!}
        title="Move Left"
        direction={HorizontalMoveDirection.LEFT}
        disabled={!moving || !canMoveLeft}
      />

      <BaseNode {...props} data={data} />

      <HorizontalMoveControl
        id={props.id}
        type="mitigating-control-node"
        index={cellIndex!}
        rowIndex={rowIndex!}
        title="Move Right"
        direction={HorizontalMoveDirection.RIGHT}
        disabled={!moving || !canMoveRight}
      />

      <AddEscalationFactorControl
        id={props.id} // mitigating control node id
        controlRecordId={recordId}
        index={cellIndex!}
        disabled={moving || editMode || !canAddEscalationFactor || hasEscalationFactors}
      />
    </div>
  );
};

export default MitigatingControlNode;
